import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Helper function to safely convert any value to a string
 */
const safeString = (value) => {
  if (typeof value === "symbol") return "";
  return value !== undefined && value !== null ? String(value) : "";
};

/**
 * SEOHead - A component to add SEO metadata to pages
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page meta description
 * @param {string} props.keywords - Comma-separated keywords
 * @param {string} props.ogTitle - Open Graph title
 * @param {string} props.ogDescription - Open Graph description
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.ogUrl - Canonical URL
 * @param {string} props.ogType - Open Graph type (default: 'article')
 * @param {string} props.twitterCard - Twitter card type (default: 'summary_large_image')
 */
const SEOHead = ({
  title = "Inforens",
  description = "Inforens is a platform for sharing knowledge and insights on various topics.",
  keywords = "Inforens, knowledge sharing, insights, articles, blogs",
  ogTitle = "Inforens",
  ogDescription = "Inforens is a platform for sharing knowledge and insights on various topics.",
  ogImage = "https://inforens.com/assets/images/logo.png",
  ogUrl = "https://inforens.com",
  ogType = "article",
  twitterCard = "summary_large_image",
}) => {
  const metaTitle = safeString(ogTitle || title);
  const metaDescription = safeString(ogDescription || description);
  const metaKeywords = safeString(keywords);
  const metaOgImage = safeString(ogImage);
  const metaOgUrl = safeString(ogUrl);
  const metaType = safeString(ogType);
  const metaTwitterCard = safeString(twitterCard);

  return (
    <Helmet>
      {/* Basic meta tags */}
      {title && <title>{safeString(title)}</title>}
      {description && <meta name="description" content={safeString(description)} />}
      {keywords && metaKeywords && <meta name="keywords" content={metaKeywords} />}

      {/* Open Graph meta tags */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={metaType} />
      {metaOgImage && <meta property="og:image" content={metaOgImage} />}
      {metaOgUrl && (
        <>
          <meta property="og:url" content={metaOgUrl} />
          <link rel="canonical" href={metaOgUrl} />
        </>
      )}

      {/* Twitter meta tags */}
      <meta name="twitter:card" content={metaTwitterCard} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {metaOgImage && <meta name="twitter:image" content={metaOgImage} />}

      {/* SEO-friendly meta tags */}
      <meta name="robots" content="index, follow" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
    </Helmet>
  );
};

export default SEOHead;
